import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const MatrixCalculator = () => {
  const [matrixA, setMatrixA] = useState({
    rows: 2,
    cols: 2,
    values: [[1, 2], [3, 4]]
  });

  const [matrixB, setMatrixB] = useState({
    rows: 2,
    cols: 2,
    values: [[5, 6], [7, 8]]
  });

  const [operation, setOperation] = useState('addition');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Initialize matrix with default values
  const initializeMatrix = (rows, cols) => {
    const matrix = [];
    for (let i = 0; i < rows; i++) {
      const row = [];
      for (let j = 0; j < cols; j++) {
        row.push(0);
      }
      matrix.push(row);
    }
    return matrix;
  };

  // Update matrix values
  const updateMatrixValue = (matrix, row, col, value) => {
    const newMatrix = JSON.parse(JSON.stringify(matrix));
    newMatrix[row][col] = parseFloat(value) || 0;
    return newMatrix;
  };

  // Matrix operations
  const calculate = () => {
    setError('');
    setResult(null);

    try {
      switch (operation) {
        case 'addition':
          if (matrixA.rows !== matrixB.rows || matrixA.cols !== matrixB.cols) {
            throw new Error('Matrices must have the same dimensions for addition');
          }
          const additionResult = matrixA.values.map((row, i) => 
            row.map((val, j) => val + matrixB.values[i][j])
          );
          setResult({
            operation: 'Addition',
            result: additionResult,
            display: 'A + B'
          });
          break;

        case 'subtraction':
          if (matrixA.rows !== matrixB.rows || matrixA.cols !== matrixB.cols) {
            throw new Error('Matrices must have the same dimensions for subtraction');
          }
          const subtractionResult = matrixA.values.map((row, i) => 
            row.map((val, j) => val - matrixB.values[i][j])
          );
          setResult({
            operation: 'Subtraction',
            result: subtractionResult,
            display: 'A - B'
          });
          break;

        case 'multiplication':
          if (matrixA.cols !== matrixB.rows) {
            throw new Error('Number of columns in matrix A must equal number of rows in matrix B');
          }
          const multiplicationResult = [];
          for (let i = 0; i < matrixA.rows; i++) {
            const row = [];
            for (let j = 0; j < matrixB.cols; j++) {
              let sum = 0;
              for (let k = 0; k < matrixA.cols; k++) {
                sum += matrixA.values[i][k] * matrixB.values[k][j];
              }
              row.push(sum);
            }
            multiplicationResult.push(row);
          }
          setResult({
            operation: 'Multiplication',
            result: multiplicationResult,
            display: 'A × B'
          });
          break;

        case 'determinant':
          if (matrixA.rows !== matrixA.cols) {
            throw new Error('Matrix must be square to calculate determinant');
          }
          if (matrixA.rows === 1) {
            const determinant = matrixA.values[0][0];
            setResult({
              operation: 'Determinant',
              result: determinant,
              display: '|A|'
            });
          } else if (matrixA.rows === 2) {
            const determinant = 
              matrixA.values[0][0] * matrixA.values[1][1] - 
              matrixA.values[0][1] * matrixA.values[1][0];
            setResult({
              operation: 'Determinant',
              result: determinant,
              display: '|A|'
            });
          } else {
            // For 3x3 and larger matrices (simplified implementation)
            const determinant = calculateDeterminant(matrixA.values);
            setResult({
              operation: 'Determinant',
              result: determinant,
              display: '|A|'
            });
          }
          break;

        case 'inverse':
          if (matrixA.rows !== matrixA.cols) {
            throw new Error('Matrix must be square to calculate inverse');
          }
          const inverse = calculateInverse(matrixA.values);
          if (!inverse) {
            throw new Error('Matrix is singular and has no inverse');
          }
          setResult({
            operation: 'Inverse',
            result: inverse,
            display: 'A⁻¹'
          });
          break;

        case 'transpose':
          const transpose = [];
          for (let j = 0; j < matrixA.cols; j++) {
            const row = [];
            for (let i = 0; i < matrixA.rows; i++) {
              row.push(matrixA.values[i][j]);
            }
            transpose.push(row);
          }
          setResult({
            operation: 'Transpose',
            result: transpose,
            display: 'Aᵀ'
          });
          break;

        default:
          throw new Error('Invalid operation selected');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Helper function to calculate determinant for n x n matrix
  const calculateDeterminant = (matrix) => {
    const n = matrix.length;
    if (n === 1) return matrix[0][0];
    if (n === 2) return matrix[0][0] * matrix[1][1] - matrix[0][1] * matrix[1][0];

    let det = 0;
    for (let col = 0; col < n; col++) {
      const subMatrix = [];
      for (let i = 1; i < n; i++) {
        const row = [];
        for (let j = 0; j < n; j++) {
          if (j !== col) row.push(matrix[i][j]);
        }
        subMatrix.push(row);
      }
      det += matrix[0][col] * Math.pow(-1, col) * calculateDeterminant(subMatrix);
    }
    return det;
  };

  // Helper function to calculate matrix inverse
  const calculateInverse = (matrix) => {
    const n = matrix.length;
    const det = calculateDeterminant(matrix);
    if (det === 0) return null;

    if (n === 1) return [[1 / matrix[0][0]]];

    const adjugate = [];
    for (let i = 0; i < n; i++) {
      const row = [];
      for (let j = 0; j < n; j++) {
        const subMatrix = [];
        for (let ii = 0; ii < n; ii++) {
          if (ii === i) continue;
          const subRow = [];
          for (let jj = 0; jj < n; jj++) {
            if (jj === j) continue;
            subRow.push(matrix[ii][jj]);
          }
          subMatrix.push(subRow);
        }
        const cofactor = Math.pow(-1, i + j) * calculateDeterminant(subMatrix);
        row.push(cofactor);
      }
      adjugate.push(row);
    }

    // Transpose the adjugate matrix
    const adjugateTranspose = [];
    for (let j = 0; j < n; j++) {
      const row = [];
      for (let i = 0; i < n; i++) {
        row.push(adjugate[i][j]);
      }
      adjugateTranspose.push(row);
    }

    // Divide by determinant
    const inverse = adjugateTranspose.map(row => 
      row.map(val => val / det)
    );

    return inverse;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-gray-100 to-blue-gray-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Matrix Calculator | Matrix Operations</title>
          <meta name="description" content="Perform various matrix operations including addition, subtraction, multiplication, determinant calculation, inverse, and transpose. This calculator supports matrices of different sizes." />
          <meta name="keywords" content="matrix calculator, matrix operations, matrix addition, matrix multiplication, determinant calculator, matrix inverse, matrix transpose, linear algebra" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-indigo-600 mr-4">🔢</div>
            <h1 className="text-3xl font-bold text-gray-800">Matrix Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Perform various matrix operations including addition, subtraction, multiplication, determinant calculation, inverse, and transpose. This calculator supports matrices of different sizes.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Matrix A</h3>
              <div className="flex items-center mb-4">
                <label className="block text-sm font-medium text-gray-700 mr-2">Rows:</label>
                <select
                  value={matrixA.rows}
                  onChange={(e) => {
                    const newRows = parseInt(e.target.value);
                    const newMatrix = initializeMatrix(newRows, matrixA.cols);
                    setMatrixA({ ...matrixA, rows: newRows, values: newMatrix });
                  }}
                  className="px-2 py-1 border border-gray-300 rounded"
                >
                  {[2, 3, 4, 5].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <label className="block text-sm font-medium text-gray-700 ml-4 mr-2">Columns:</label>
                <select
                  value={matrixA.cols}
                  onChange={(e) => {
                    const newCols = parseInt(e.target.value);
                    const newMatrix = initializeMatrix(matrixA.rows, newCols);
                    setMatrixA({ ...matrixA, cols: newCols, values: newMatrix });
                  }}
                  className="px-2 py-1 border border-gray-300 rounded"
                >
                  {[2, 3, 4, 5].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <div className="border border-gray-300 rounded overflow-hidden">
                {matrixA.values.map((row, i) => (
                  <div key={i} className="flex border-b border-gray-300 last:border-b-0">
                    {row.map((val, j) => (
                      <input
                        key={j}
                        type="number"
                        value={val}
                        onChange={(e) => {
                          const newMatrix = updateMatrixValue(matrixA.values, i, j, e.target.value);
                          setMatrixA({ ...matrixA, values: newMatrix });
                        }}
                        className="w-16 px-2 py-1 text-center border-r border-gray-300 last:border-r-0 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Matrix B</h3>
              <div className="flex items-center mb-4">
                <label className="block text-sm font-medium text-gray-700 mr-2">Rows:</label>
                <select
                  value={matrixB.rows}
                  onChange={(e) => {
                    const newRows = parseInt(e.target.value);
                    const newMatrix = initializeMatrix(newRows, matrixB.cols);
                    setMatrixB({ ...matrixB, rows: newRows, values: newMatrix });
                  }}
                  className="px-2 py-1 border border-gray-300 rounded"
                >
                  {[2, 3, 4, 5].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <label className="block text-sm font-medium text-gray-700 ml-4 mr-2">Columns:</label>
                <select
                  value={matrixB.cols}
                  onChange={(e) => {
                    const newCols = parseInt(e.target.value);
                    const newMatrix = initializeMatrix(matrixB.rows, newCols);
                    setMatrixB({ ...matrixB, cols: newCols, values: newMatrix });
                  }}
                  className="px-2 py-1 border border-gray-300 rounded"
                >
                  {[2, 3, 4, 5].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <div className="border border-gray-300 rounded overflow-hidden">
                {matrixB.values.map((row, i) => (
                  <div key={i} className="flex border-b border-gray-300 last:border-b-0">
                    {row.map((val, j) => (
                      <input
                        key={j}
                        type="number"
                        value={val}
                        onChange={(e) => {
                          const newMatrix = updateMatrixValue(matrixB.values, i, j, e.target.value);
                          setMatrixB({ ...matrixB, values: newMatrix });
                        }}
                        className="w-16 px-2 py-1 text-center border-r border-gray-300 last:border-r-0 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Operation:</label>
            <select
              value={operation}
              onChange={(e) => setOperation(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="addition">Addition (A + B)</option>
              <option value="subtraction">Subtraction (A - B)</option>
              <option value="multiplication">Multiplication (A × B)</option>
              <option value="determinant">Determinant (|A|)</option>
              <option value="inverse">Inverse (A⁻¹)</option>
              <option value="transpose">Transpose (Aᵀ)</option>
            </select>
          </div>

          <button
            onClick={calculate}
            className="w-full py-3 px-4 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Result</h2>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">{result.operation}</h3>
                <p className="text-lg font-medium mb-4">{result.display}</p>

                {typeof result.result === 'number' ? (
                  <div className="text-2xl font-bold text-indigo-600 mt-4">
                    {result.result.toFixed(4)}
                  </div>
                ) : (
                  <div className="border border-gray-300 rounded overflow-hidden mt-4">
                    {result.result.map((row, i) => (
                      <div key={i} className="flex border-b border-gray-300 last:border-b-0">
                        {row.map((val, j) => (
                          <div
                            key={j}
                            className="w-24 px-2 py-1 text-center border-r border-gray-300 last:border-r-0"
                          >
                            {val.toFixed(4)}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* How to Use Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-indigo-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Matrix Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Set Matrix Dimensions:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Adjust the rows and columns for Matrix A and B using the dropdowns</li>
                </ul>
              </li>
              <li><strong>Enter Matrix Values:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input numerical values in the matrix cells</li>
                  <li>Empty cells will be treated as zeros</li>
                </ul>
              </li>
              <li><strong>Select Operation:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose the desired matrix operation from the dropdown</li>
                </ul>
              </li>
              <li><strong>Calculate:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Click the Calculate button to perform the operation</li>
                  <li>View the result matrix or scalar value</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-indigo-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>For matrix multiplication, ensure the number of columns in A matches the rows in B</li>
                <li>Determinant and inverse operations require square matrices</li>
                <li>Transpose operation works for any matrix size</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Matrix Operations Guide <span className="text-indigo-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              This calculator supports the following matrix operations:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Addition:</strong> Element-wise addition of two matrices of the same dimensions</li>
              <li><strong>Subtraction:</strong> Element-wise subtraction of two matrices of the same dimensions</li>
              <li><strong>Multiplication:</strong> Matrix multiplication (A × B) where columns of A match rows of B</li>
              <li><strong>Determinant:</strong> Calculates the determinant of a square matrix</li>
              <li><strong>Inverse:</strong> Calculates the inverse of a square, non-singular matrix</li>
              <li><strong>Transpose:</strong> Flips a matrix over its diagonal, switching rows and columns</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-indigo-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Check Dimensions:</strong> Always verify matrix dimensions before performing operations</li>
              <li><strong>Validate Results:</strong> For critical applications, verify results with alternative methods</li>
              <li><strong>Use Square Matrices:</strong> For determinant and inverse calculations</li>
              <li><strong>Understand Limitations:</strong> Some matrices (singular) don't have inverses</li>
            </ul>
            <p className="italic mt-4">
              Note: For large matrices or complex operations, consider using specialized mathematical software.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MatrixCalculator;